import {
  createMongoAbility,
  subject as caslSubject,
  type MongoAbility,
  type RawRuleOf,
} from '@casl/ability';
import { unpackRules, type PackRule } from '@casl/ability/extra';
import { useAbility as useCaslAbility } from '@casl/react';

import type { components } from '@/types/api';
import { Sentry } from '@/util/sentry';

type GeneratedAbility = NonNullable<
  components['schemas']['UserAbilitiesAdminResponse']['data']
>['abilities'][number];

export type AbilityAction = GeneratedAbility['action'];

export type AbilitySubject = GeneratedAbility['subject'];

export type Ability = MongoAbility<[AbilityAction, AbilitySubject]>;

type PackedAbilityRule = PackRule<RawRuleOf<Ability>>;

/**
 * Unpacks the packed `rules` the backend returns on `GET /v2/users/me`. Yields
 * no rules (fail-closed) when `rules` is missing or malformed rather than
 * throwing — this only gates UI affordances, the server remains the real
 * enforcement point.
 */
export function unpackAbilityRules(rules: unknown): RawRuleOf<Ability>[] {
  try {
    if (!Array.isArray(rules)) {
      return [];
    }

    return unpackRules(rules as PackedAbilityRule[]);
  } catch {
    return [];
  }
}

/**
 * Builds an ability from packed rules. Missing or malformed rules yield an
 * ability with no grants, which is also how the provider seeds itself before
 * the current-user query has produced anything.
 */
export function buildAbilityFromRules(rules: unknown): Ability {
  return createMongoAbility(unpackAbilityRules(rules));
}

/**
 * Reasons the server attaches to the rules it denies an account that has not
 * finished confirmation. Mirrors `DenialReason` in primum-infra's
 * `@primum/account-state`, which this app does not depend on, so the two must
 * be kept in step by hand. Any other reason, such as a feature gate, is not a
 * confirmation restriction.
 */
export const CONFIRMATION_DENIAL_REASONS: readonly string[] = ['NPI_REQUIRED', 'NPI_PENDING'];

/**
 * Why the account may not take an action, when the server says that is because
 * it has not finished confirmation. `undefined` when the action is allowed, or
 * denied for any other reason. Read off the denying rule, the same one the
 * server uses to word its 403.
 */
export function confirmationDenialReasonFor(
  ability: Ability,
  action: AbilityAction,
  subject: AbilitySubject
): string | undefined {
  if (ability.can(action, subject)) {
    return undefined;
  }

  const reason = ability.relevantRuleFor(action, subject)?.reason;

  return reason && CONFIRMATION_DENIAL_REASONS.includes(reason) ? reason : undefined;
}

/**
 * Why the account may not submit an inquiry, when that is because it has not
 * finished confirmation. Submitting is the capability the routing gates read,
 * since every unconfirmed account is denied it.
 */
export function confirmationDenialReason(ability: Ability): string | undefined {
  return confirmationDenialReasonFor(ability, 'submit', 'Inquiry');
}

/**
 * Whether an inquiry composer should send the reader to NPI verification. True
 * when the server denies `submit Inquiry` for a confirmation reason, and also
 * while no rules have arrived yet, so the composer stays gated during the first
 * load instead of flashing an enabled submit. A denial for any other reason is
 * not a verification problem: the composer should disable submit for it without
 * pointing the reader at a screen that cannot help.
 */
export function needsVerificationToSubmitInquiry(ability: Ability): boolean {
  if (ability.can('submit', 'Inquiry')) {
    return false;
  }

  return ability.rules.length === 0 || confirmationDenialReason(ability) !== undefined;
}

/**
 * Tags a plain object as a CASL subject instance so a record-scoped rule
 * condition (e.g. the backend's `attest`/`Inquiry` rule, scoped to
 * `{ recipientId }`) evaluates against the object's real fields instead of
 * only matching by bare subject type. `AbilitySubject` above is kept as plain
 * string literals — mirroring the backend enum 1:1 rather than modeling each
 * subject's shape through CASL's `InferSubjects` — so this cast is the one
 * place that simplification is paid for; callers still pass real objects.
 *
 * Tags a shallow copy, never the object passed in. CASL marks its subject by
 * adding a property, and React's props and other frozen objects refuse one:
 * tagging an inbox card's props threw and took the card down.
 */
export function abilitySubject(type: AbilitySubject, instance: object): AbilitySubject {
  return caslSubject(type, { ...instance }) as unknown as AbilitySubject;
}

/**
 * Denies everything. The reference is module-level so the fallback below hands
 * out a stable ability rather than a fresh one per render.
 */
const DENY_ALL: Ability = createMongoAbility([]);

/**
 * The current user's server-driven ability, published by `AppAbilityProvider`.
 * Re-renders the caller whenever the rules change. Wraps `@casl/react`'s hook to
 * pin the generic, so no call site can omit it and fall back to CASL's untyped
 * `AnyAbility`.
 *
 * `@casl/react` throws when no provider sits above the caller. That is caught
 * here and turned into a deny-all, because this only gates UI affordances while
 * the server is the real enforcement point: the worst a stale-mounted caller
 * should cost is a hidden control, not a blank screen for the whole route. The
 * throw still reaches Sentry, so the missing provider is loud where it matters
 * and silent where it doesn't.
 *
 * Catching around a hook is safe here specifically: CASL runs all of its hooks
 * (`useContext`, two `useCallback`s, `useSyncExternalStore`) before it throws,
 * so hook order is identical on both paths.
 *
 * Pass records through {@link abilitySubject} for record-scoped checks, e.g.
 * `ability.can('attest', abilitySubject('Inquiry', inquiry))`.
 */
export function useAbility(): Ability {
  try {
    return useCaslAbility<Ability>();
  } catch (error) {
    Sentry.captureException(error);

    return DENY_ALL;
  }
}
